import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'How It Works', path: '/how-it-works' },
    { name: 'Plans', path: '/#plans' },
    { name: 'Features', path: '/#features' },
    { name: 'Pet Care', path: '/pet-care' },
    { name: 'Privacy', path: '/#privacy' },
  ];

  // Scroll to element with offset for fixed header
  const scrollToElement = (elementId) => {
    const element = document.getElementById(elementId);
    if (element) {
      const headerOffset = 100; // Account for fixed header height
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  const handleNavClick = (path, name) => {
    setIsMobileMenuOpen(false);
    
    // Home - always scroll to top
    if (name === 'Home') {
      if (location.pathname === '/') {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        navigate('/');
        setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
      }
      return;
    }
    
    // Handle anchor links (Plans, Features, Privacy)
    if (path.startsWith('/#')) {
      const id = path.replace('/#', '');
      
      if (location.pathname === '/') {
        // Already on home page - scroll to section
        scrollToElement(id);
      } else {
        // Navigate to home page first, then scroll
        navigate('/');
        setTimeout(() => scrollToElement(id), 150);
      }
      return;
    }
  };

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled 
          ? 'bg-[#FAF8F5]/95 backdrop-blur-xl shadow-sm' 
          : 'bg-transparent'
      }`}
      data-testid="navigation"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-[72px] md:h-[80px]">
          {/* Logo */}
          <Link 
            to="/" 
            onClick={() => {
              if (location.pathname === '/') {
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }
            }}
            className="flex items-center py-6" 
            data-testid="nav-logo"
          >
            <span className="text-xl font-semibold tracking-[0.15em] text-[#1A1A1A]">
              CONNECT
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              link.path.startsWith('/#') || link.name === 'Home' ? (
                <button
                  key={link.path}
                  onClick={() => handleNavClick(link.path, link.name)}
                  className={`nav-link text-sm ${
                    location.pathname === '/' && link.name === 'Home' ? 'text-[#1A1A1A]' : ''
                  }`}
                  data-testid={`nav-link-${link.name.toLowerCase().replace(' ', '-')}`}
                >
                  {link.name}
                </button>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`nav-link text-sm ${
                    location.pathname === link.path ? 'text-[#1A1A1A]' : ''
                  }`}
                  data-testid={`nav-link-${link.name.toLowerCase().replace(' ', '-')}`}
                >
                  {link.name}
                </Link>
              )
            ))}
          </nav>

          {/* Desktop CTA */}
          <div className="hidden lg:block">
            <Link
              to="/reserve"
              className="bg-[#1A1A1A] text-white px-6 py-2.5 rounded-full text-sm font-medium 
                         hover:bg-[#333] transition-all duration-300 hover:scale-105 inline-block"
              data-testid="nav-reserve-btn"
            >
              Reserve Yours
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 text-[#1A1A1A]"
            data-testid="mobile-menu-btn"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-[#FAF8F5]/98 backdrop-blur-xl border-t border-black/5"
            data-testid="mobile-menu"
          >
            <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col gap-4">
              {navLinks.map((link) => (
                link.path.startsWith('/#') || link.name === 'Home' ? (
                  <button
                    key={link.path}
                    onClick={() => handleNavClick(link.path, link.name)}
                    className={`py-2 text-lg text-left ${
                      location.pathname === '/' && link.name === 'Home' 
                        ? 'text-[#1A1A1A]' 
                        : 'text-[#5A5A5A] hover:text-[#1A1A1A]'
                    }`}
                  >
                    {link.name}
                  </button>
                ) : (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`py-2 text-lg ${
                      location.pathname === link.path
                        ? 'text-[#1A1A1A]'
                        : 'text-[#5A5A5A]'
                    }`}
                  >
                    {link.name}
                  </Link>
                )
              ))}
              <Link
                to="/reserve"
                onClick={() => setIsMobileMenuOpen(false)}
                className="bg-[#1A1A1A] text-white px-6 py-3 rounded-full text-sm font-medium 
                           mt-2 w-full hover:bg-[#333] transition-all text-center block"
              >
                Reserve Yours
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Navigation;
